// src/HomePage/Game/Werewolf/WerewolfHistoryView.js
import React, { useState, useEffect } from 'react';
import { useWerewolf } from './WerewolfContext';
import { ROLE_DEFINITIONS } from './roleEngine';
import './WerewolfHistoryView.css';

const WerewolfHistoryView = ({ onBack }) => {
  const { fetchMyGameHistory, activeUid } = useWerewolf();
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      const data = await fetchMyGameHistory();
      setHistoryList(data);
      setLoading(false);
    };
    loadHistory();
  }, [activeUid]);

  const toggleDayExpand = (dayNum) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayNum]: prev[dayNum] === undefined ? false : !prev[dayNum]
    }));
  };

  if (loading) {
    return (
      <div className="ww-history-loading">
        <div className="ww-spinner">🔮</div>
        <p>Đang tải lịch sử các ván game của bạn...</p>
      </div>
    );
  }

  // MÀN HÌNH CHI TIẾT VÁN ĐẤU
  if (selectedGame) {
    const playersMap = selectedGame.players || {};
    const myPlayer = playersMap[activeUid];
    const cupidCouple = selectedGame.nightActions?.cupidCouple || [];
    const winnerTeam = selectedGame.winnerTeam;

    let isMyWin = false;
    if (winnerTeam === 'COUPLE') {
      isMyWin = cupidCouple?.includes(activeUid);
    } else if (winnerTeam === 'WOLF') {
      isMyWin = myPlayer?.role === 'WEREWOLF';
    } else if (winnerTeam === 'HUMAN') {
      isMyWin = myPlayer?.role !== 'WEREWOLF';
    }

    const groupedLogs = (selectedGame.gameLogs || []).reduce((acc, log) => {
      const d = log.day || 1;
      if (!acc[d]) acc[d] = [];
      acc[d].push(log);
      return acc;
    }, {});

    return (
      <div className="ww-history-detail-container animate-fade-in">
        <div className="ww-history-detail-header">
          <button className="ww-btn secondary" onClick={() => setSelectedGame(null)}>
            ⬅️ Quay lại danh sách lịch sử
          </button>
          <h3>{selectedGame.roomName || 'Phòng Ma Sói'}</h3>
          <span className="ww-history-date">
            📅 {new Date(selectedGame.endedAt).toLocaleString('vi-VN')}
          </span>
        </div>

        {/* Kết quả ván đấu */}
        <div className="ww-game-over-header">
          <div className={`ww-winner-announcement ${winnerTeam?.toLowerCase()}`}>
            <h3>{selectedGame.winnerMsg}</h3>
          </div>
          <div className={`ww-personal-result-badge ${isMyWin ? 'victory' : 'defeat'}`}>
            {isMyWin ? '🎉 BẠN ĐÃ CHIẾN THẮNG!' : '💀 BẠN ĐÃ THẤT BẠI!'}
          </div>
        </div>

        {/* Danh sách người chơi và vai trò thật */}
        <div className="ww-history-players-section">
          <h4>👥 Danh Sách Người Chơi & Vai Trò Thật</h4>
          <div className="ww-history-players-grid">
            {Object.values(playersMap).map(p => {
              const roleInfo = ROLE_DEFINITIONS[p.role] || { name: 'Chưa rõ', icon: '❓', team: 'Chưa rõ' };
              const isMe = p.uid === activeUid;
              return (
                <div key={p.uid} className={`ww-history-player-card ${isMe ? 'is-me' : ''}`}>
                  <span className="ww-seat-tag">#{p.seatIndex}</span>
                  <div className="ww-history-avatar">{p.avatar || '👤'}</div>
                  <div className="ww-history-player-info">
                    <span className="ww-player-holy">{p.holyName}</span>
                    <span className="ww-player-full">{p.fullName}</span>
                  </div>
                  <div className="ww-history-role-pill">
                    <span>{roleInfo.icon} {roleInfo.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Lịch sử tiến trình nhóm theo ngày */}
        <div className="ww-grouped-logs-container">
          <h4>📜 Tiến Trình Chi Tiết Các Ngày</h4>
          {Object.entries(groupedLogs).map(([dayNum, logs]) => {
            const isExpanded = expandedDays[dayNum] !== false;
            return (
              <div key={dayNum} className="ww-day-log-group">
                <button
                  type="button"
                  className="ww-day-group-header"
                  onClick={() => toggleDayExpand(dayNum)}
                >
                  <span>🗓️ NGÀY {dayNum} ({logs.length} sự kiện)</span>
                  <span>{isExpanded ? '🔽 Thu gọn' : '▶️ Mở xem'}</span>
                </button>

                {isExpanded && (
                  <div className="ww-day-logs-list">
                    {logs.map((log) => (
                      <div key={log.id} className="ww-log-item">
                        <span className="ww-log-phase">{log.phase}</span>
                        <p className="ww-log-content">{log.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // MÀN HÌNH DANH SÁCH CÁC VÁN GAME ĐÃ THAM GIA
  return (
    <div className="ww-history-list-container animate-fade-in">
      <div className="ww-history-header-card">
        <div className="header-left">
          <h3>📜 Lịch Sử Ván Game Đã Tham Gia</h3>
          <p>Danh sách các trận đấu Ma Sói ghi nhận tài khoản của bạn</p>
        </div>
        <button className="ww-btn primary" onClick={onBack}>
          🎮 Quay lại Bàn Chơi
        </button>
      </div>

      {historyList.length === 0 ? (
        <div className="ww-history-empty">
          <span className="empty-icon">🕸️</span>
          <p>Bạn chưa tham gia ván game Ma Sói nào hoàn chỉnh.</p>
        </div>
      ) : (
        <div className="ww-history-cards-grid">
          {historyList.map(game => {
            const myPlayer = game.players?.[activeUid];
            const myRoleInfo = myPlayer ? ROLE_DEFINITIONS[myPlayer.role] : null;

            return (
              <div
                key={game.historyId}
                className="ww-history-summary-card"
                onClick={() => setSelectedGame(game)}
              >
                <div className="summary-card-top">
                  <h4>{game.roomName || 'Phòng Ma Sói'}</h4>
                  <span className="summary-date">
                    {new Date(game.endedAt).toLocaleDateString('vi-VN')}
                  </span>
                </div>

                <div className="summary-card-body">
                  <p className="winner-msg">{game.winnerMsg}</p>
                  {myRoleInfo && (
                    <div className="my-role-badge">
                      <span>Vai trò của bạn: <strong>{myRoleInfo.icon} {myRoleInfo.name}</strong></span>
                    </div>
                  )}
                </div>

                <div className="summary-card-footer">
                  <span>👥 {Object.keys(game.players || {}).length} Người chơi</span>
                  <button className="ww-btn-action-sm">👁️ Xem chi tiết</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WerewolfHistoryView;